import { Component, Injector, Input, OnDestroy, OnInit } from "@angular/core";
import { Subject } from "rxjs";
import { finalize, take, takeUntil } from "rxjs/operators";
import { BaseElement } from "../../base-element.abstract";
import {
  selectHidden,
  selectMemberContactInfo,
  selectSelectedBlockId,
} from "../../+state";
import { HideElementAction } from "../../+state/elements/elements.actions";
import { SurveyElement, SurveyFacade } from "../../../public-api";
import { getElementId } from "../../+state/elements/elements.reducer";
import { get } from "lodash";
import {
  LoadMemberContact,
  SendContactAction,
  SurveyActionTypes,
} from "../../+state/survey/survey.actions";
import { Actions, ofType } from "@ngrx/effects";

interface MemberContact {
  contactId: string;
  contactText: string;
  isShareLinkDnc: boolean;
  isValidFormat: boolean;
}
interface MemberContactInfo {
  channel: string;
  contacts: MemberContact[];
}
@Component({
  selector: "surveys-element-dialog",
  templateUrl: "./element-dialog.component.html",
  styleUrls: ["./element-dialog.component.scss"],
})
export class ElementDialogComponent
  extends BaseElement
  implements OnInit, OnDestroy
{
  @Input() public element: SurveyElement;

  public questionElement: SurveyElement["question"] & { id: string };
  public defaultValue = "";
  public showDialog = false;
  public messageSent = false;
  public headerTitle = "";
  public subHeaderTitle = "";
  public memberContactInfo: MemberContactInfo[];
  public isLoading = false;
  public currentBlockId: string;
  public isLoadingContact = false;

  private readonly destroy$ = new Subject<void>();
  private elementId: string;
  public contacts: MemberContact[] = [];

  constructor(
    injector: Injector,
    private actions$: Actions,
    private surveyFacade: SurveyFacade,
  ) {
    super(injector);
  }

  public ngOnInit(): void {
    this.elementId = getElementId(this.element);
    this.initializeDialog();
    this.subscribeToHiddenState();
    this.subscribeToMemberContactInfo();
    this.setupQuestionElement();
    this.setupHeaderTitles();
    this.subscribeToContactLoadComplete();
    this.store
      .select(selectSelectedBlockId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((blockId: string) => {
        this.currentBlockId = blockId;
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getContact(): void {
    this.isLoadingContact = true;
    const memberId = this.surveyFacade.getEnrollmentId();
    const shortCode = this.surveyFacade.getSurveyContext();
    this.store.dispatch(
      new LoadMemberContact({
        memberId,
        shortCode,
      }),
    );
  }

  private initializeDialog(): void {
    this.store.dispatch(new HideElementAction(this.elementId));
  }

  private subscribeToHiddenState(): void {
    this.store
      .select(selectHidden)
      .pipe(takeUntil(this.destroy$))
      .subscribe((hidden: string[]) => {
        const wasShown = this.showDialog;
        this.showDialog = !hidden.includes(this.element.id);

        // Call API when dialog opens (transitions from false to true)
        if (!wasShown && this.showDialog) {
          this.getContact();
        }
      });
  }

  private subscribeToMemberContactInfo(): void {
    this.store
      .select(selectMemberContactInfo)
      .pipe(takeUntil(this.destroy$))
      .subscribe((memberContactInfo: MemberContactInfo[]) => {
        const contactInfo = memberContactInfo?.find(
          (info) => info.channel === this.element.question.type,
        );
        this.contacts =
          contactInfo?.contacts?.filter((contact) => !contact.isShareLinkDnc) ??
          [];
        this.defaultValue = this.contacts[0]?.contactId ?? "";
        this.memberContactInfo = memberContactInfo;
      });
  }

  private setupQuestionElement(): void {
    this.questionElement = {
      ...this.element.question,
      id: this.elementId,
    };
  }

  private setupHeaderTitles(): void {
    this.headerTitle = get(this.element, `header.${this.language}`, "Header");
    this.subHeaderTitle = get(this.element, `subheader.${this.language}`, "");
  }

  protected languageChanged(): void {
    if (!this.element) {
      return;
    }

    this.setupHeaderTitles();
  }

  public onClose(): void {
    this.messageSent = false;
    this.store.dispatch(new HideElementAction(this.elementId));
  }

  private subscribeToContactLoadComplete(): void {
    this.actions$
      .pipe(
        ofType(
          SurveyActionTypes.MEMBER_CONTACT_LOADED,
          SurveyActionTypes.SURVEY_LOAD_FAILED,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.isLoadingContact = false;
      });
  }

  public onConfirm(): void {
    this.isLoading = true;
    const shortCode = this.surveyFacade.getSurveyContext();

    const payload = {
      blockId: this.currentBlockId,
      linkCode: shortCode,
      contactId: this.defaultValue,
      channelType: this.element.question.type,
      shareAt: new Date().toUTCString(),
    };
    this.store.dispatch(
      new SendContactAction({
        memberId: this.surveyFacade.getEnrollmentId(),
        shortCode,
        data: payload,
      }),
    );
    this.actions$
      .pipe(
        ofType(SurveyActionTypes.SEND_CONTACT_FAILURE),
        take(1),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
    this.actions$
      .pipe(
        ofType(SurveyActionTypes.SEND_CONTACT_SUCCESS),
        take(1),
        finalize(() => {
          this.isLoading = false;
          this.messageSent = true;
        }),
      )
      .subscribe();
  }

  public get showUnsubscribedHelperText(): boolean {
    return (
      !!this.unsubscribedHelperTextKey &&
      !this.isLoadingContact &&
      !this.defaultValue
    );
  }

  public get unsubscribedHelperTextKey(): string {
    const channelType = this.element?.question?.type;

    if (channelType === "SMS") {
      return "surveys.dialog.unsubscribedHelperSms";
    }

    if (channelType === "EMAIL") {
      return "surveys.dialog.unsubscribedHelperEmail";
    }

    return "";
  }
}
